import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HiCash,
  HiRefresh,
  HiSearch,
} from 'react-icons/hi';
import apiService from '../services/api';
import { normalizeImageUrl } from '../utils/apiHelpers';
import { formatMoney } from '../utils/currencies';
import { useNotification } from '../hooks/useNotification';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import Notification from './Notification';
import Select from './Select';
import './PagamentosRetirada.css';

const formatBrl = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_PENDENCIA = {
  pendente: { label: 'Pendente', className: 'is-pendente' },
  pago: { label: 'Pago', className: 'is-pago' },
  cancelado: { label: 'Cancelado', className: 'is-cancelado' },
};

const STATUS_PAGAMENTO = {
  Pago: { label: 'Pago', className: 'is-pago' },
  Incompleto: { label: 'Incompleto', className: 'is-incompleto' },
  Pendente: { label: 'Pendente', className: 'is-pendente' },
  Cancelado: { label: 'Cancelado', className: 'is-cancelado' },
};

const PagamentosRetirada = () => {
  const { notifications, removeNotification, showWarning, showError } =
    useNotification();

  const [tab, setTab] = useState('retirada'); // 'retirada' | 'registrados'
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });
  const [page, setPage] = useState(1);

  const statusOptions = useMemo(() => {
    if (tab === 'retirada') {
      return [
        { value: 'todos', label: 'Todos os status' },
        { value: 'pendente', label: 'Pendente' },
        { value: 'pago', label: 'Pago' },
        { value: 'cancelado', label: 'Cancelado' },
      ];
    }
    return [
      { value: 'todos', label: 'Todos os status' },
      { value: 'Pago', label: 'Pago' },
      { value: 'Incompleto', label: 'Incompleto' },
      { value: 'Cancelado', label: 'Cancelado' },
    ];
  }, [tab]);

  const loadData = useCallback(async (opts = {}) => {
    try {
      if (!opts.silent) setLoading(true);
      const params = {
        page,
        per_page: 15,
        search: search.trim() || undefined,
        status: statusFilter !== 'todos' ? statusFilter : undefined,
      };

      const response =
        tab === 'retirada'
          ? await apiService.listPendenciasPagamento(params)
          : await apiService.listPagamentos(params);

      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setItems(list);
      setMeta(
        response?.meta || {
          current_page: page,
          last_page: 1,
          per_page: 15,
          total: list.length,
        }
      );
    } catch (error) {
      showError(error?.message || 'Erro ao carregar pagamentos');
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showError is stable enough; avoid reload loops
  }, [tab, page, search, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useRealtimeRefresh(['payments', 'packages', 'cobrancas'], () =>
    loadData({ silent: true })
  );

  useEffect(() => {
    setPage(1);
    setStatusFilter('todos');
    setSearch('');
  }, [tab]);

  const openProof = (url) => {
    const abs = normalizeImageUrl(url);
    if (!abs) {
      showWarning('Comprovante indisponível');
      return;
    }
    window.open(abs, '_blank');
  };

  return (
    <div className="page-container pagamentos-page">
      <header className="pagamentos-hero">
        <div className="pagamentos-hero__left">
          <span className="pagamentos-hero__icon" aria-hidden>
            <HiCash size={26} />
          </span>
          <div>
            <h1>Pagamentos</h1>
            <p>
              Visualize pagamentos registrados e valores cobrados na retirada.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="pagamentos-refresh"
          onClick={loadData}
          disabled={loading}
        >
          <HiRefresh size={18} className={loading ? 'is-spinning' : ''} />
          Atualizar
        </button>
      </header>

      <div className="pagamentos-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'retirada'}
          className={`pagamentos-tab${tab === 'retirada' ? ' is-active' : ''}`}
          onClick={() => setTab('retirada')}
        >
          Na retirada
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'registrados'}
          className={`pagamentos-tab${tab === 'registrados' ? ' is-active' : ''}`}
          onClick={() => setTab('registrados')}
        >
          Registrados
        </button>
      </div>

      <div className="pagamentos-filters">
        <div className="pagamentos-search">
          <HiSearch size={18} aria-hidden />
          <input
            type="search"
            placeholder="Buscar por pacote, destinatário…"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setPage(1);
            setStatusFilter(e.target.value);
          }}
          options={statusOptions}
        />
      </div>

      <div className="pagamentos-meta">
        <span>
          {meta.total || 0} registro{(meta.total || 0) === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <div className="pagamentos-empty">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="pagamentos-empty">
          Nenhum pagamento encontrado com os filtros atuais.
        </div>
      ) : (
        <div className="pagamentos-list">
          {tab === 'retirada'
            ? items.map((item) => {
                const st =
                  STATUS_PENDENCIA[item.status] || STATUS_PENDENCIA.pendente;
                const code =
                  item.pacote?.codigo_pacote || `Pacote #${item.pacote_id}`;
                const dest = item.pacote?.nome_destinatario || '—';
                const metaLine = [
                  String(item.moeda_local || '').toUpperCase(),
                  item.data_cotacao ? `cotação ${item.data_cotacao}` : null,
                  item.status === 'pago'
                    ? formatDateTime(item.pago_em)
                    : item.criado_por?.nome
                      ? `por ${item.criado_por.nome}`
                      : null,
                ]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <article key={item.id} className="pagamentos-row">
                    {item.foto_pagamento_url ? (
                      <button
                        type="button"
                        className="pagamentos-row__thumb"
                        onClick={() => openProof(item.foto_pagamento_url)}
                        title="Ver comprovante"
                      >
                        <img
                          src={normalizeImageUrl(item.foto_pagamento_url)}
                          alt="Comprovante"
                        />
                      </button>
                    ) : (
                      <div className="pagamentos-row__thumb pagamentos-row__thumb--empty" aria-hidden>
                        <HiCash size={16} />
                      </div>
                    )}

                    <div className="pagamentos-row__body">
                      <div className="pagamentos-row__top">
                        <strong className="pagamentos-row__code">{code}</strong>
                        <span className={`pagamentos-badge ${st.className}`}>
                          {st.label}
                        </span>
                      </div>
                      <div className="pagamentos-row__dest">{dest}</div>
                      <div className="pagamentos-row__meta">{metaLine}</div>
                      {item.observacao && (
                        <div className="pagamentos-row__obs">{item.observacao}</div>
                      )}
                    </div>

                    <div className="pagamentos-row__value">
                      <strong>
                        {formatMoney(item.valor_local, item.moeda_local)}
                      </strong>
                      <span>{formatBrl(item.valor_brl)}</span>
                    </div>
                  </article>
                );
              })
            : items.map((item) => {
                const st =
                  STATUS_PAGAMENTO[item.status] || {
                    label: item.status || '—',
                    className: 'is-pendente',
                  };
                const proofs = Array.isArray(item.comprovantes)
                  ? item.comprovantes
                  : [];
                const firstProof = proofs[0];
                const code =
                  item.pacote?.codigo_pacote ||
                  (item.pacote_id ? `Pacote #${item.pacote_id}` : 'Sem pacote');
                const dest = item.pacote?.nome_destinatario || '—';
                const metaLine = [
                  item.metodo_pagamento || null,
                  formatDateTime(item.data_pagamento),
                  item.registrado_por?.nome
                    ? `por ${item.registrado_por.nome}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <article key={item.id} className="pagamentos-row">
                    {firstProof?.url ? (
                      <button
                        type="button"
                        className="pagamentos-row__thumb"
                        onClick={() => openProof(firstProof.url)}
                        title={firstProof.nome || 'Ver comprovante'}
                      >
                        {/\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(
                          String(firstProof.url || '')
                        ) ? (
                          <img
                            src={normalizeImageUrl(firstProof.url)}
                            alt={firstProof.nome || 'Comprovante'}
                          />
                        ) : (
                          <span>PDF</span>
                        )}
                      </button>
                    ) : (
                      <div className="pagamentos-row__thumb pagamentos-row__thumb--empty" aria-hidden>
                        <HiCash size={16} />
                      </div>
                    )}

                    <div className="pagamentos-row__body">
                      <div className="pagamentos-row__top">
                        <strong className="pagamentos-row__code">{code}</strong>
                        <span className={`pagamentos-badge ${st.className}`}>
                          {st.label}
                        </span>
                      </div>
                      <div className="pagamentos-row__dest">{dest}</div>
                      <div className="pagamentos-row__meta">{metaLine}</div>
                      {item.descricao && (
                        <div className="pagamentos-row__obs">{item.descricao}</div>
                      )}
                      {proofs.length > 1 && (
                        <div className="pagamentos-row__proofs">
                          {proofs.slice(1).map((comp, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className="pagamentos-row__proof-mini"
                              onClick={() => openProof(comp.url)}
                              title={comp.nome || 'Comprovante'}
                            >
                              {/\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(
                                String(comp.url || '')
                              ) ? (
                                <img
                                  src={normalizeImageUrl(comp.url)}
                                  alt={comp.nome || `Comprovante ${idx + 2}`}
                                />
                              ) : (
                                <span>PDF</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pagamentos-row__value">
                      <strong>{formatBrl(item.valor)}</strong>
                      <span>{item.metodo_pagamento || 'pagamento'}</span>
                    </div>
                  </article>
                );
              })}
        </div>
      )}

      {meta.last_page > 1 && (
        <div className="pagamentos-pager">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span>
            Página {meta.current_page} de {meta.last_page}
          </span>
          <button
            type="button"
            disabled={page >= meta.last_page || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </button>
        </div>
      )}

      <div className="notifications-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default PagamentosRetirada;
